import React from 'react';
import type { Store as StoreType } from '../../../../services/authService';
import activityFeedService, { type ActivityFeedItem } from '../../../../services/activityFeedService';
import {
    ACTIVITY_FEED_EVENT_CATEGORY_OPTIONS,
    ACTIVITY_FEED_EVENT_TYPE_OPTIONS,
} from '../../../../utils/activityFeedFilterOptions';
import { getAvatarColor, getInitials } from '../utils/storeDetailsOverviewUtils';
import { useStoreDetailsOverviewAgent } from './useStoreDetailsOverviewAgent';
import { useStoreDetailsOverviewLotto } from './useStoreDetailsOverviewLotto';
import { useStoreDetailsOverviewPos } from './useStoreDetailsOverviewPos';
import { useStoreDetailsOverviewSyncGames } from './useStoreDetailsOverviewSyncGames';

type UseStoreDetailsOverviewParams = {
    selectedPosType: string;
    store: StoreType | null;
    onSilentStoreRefresh?: () => Promise<StoreType | null> | StoreType | null | void;
};

export const useStoreDetailsOverview = ({
    selectedPosType,
    store,
    onSilentStoreRefresh,
}: UseStoreDetailsOverviewParams) => {
    const [activityFeed, setActivityFeed] = React.useState<ActivityFeedItem[]>([]);
    const [activityFeedLoading, setActivityFeedLoading] = React.useState(false);
    const [activityFeedLoadingMore, setActivityFeedLoadingMore] = React.useState(false);
    const [activityFeedHasMore, setActivityFeedHasMore] = React.useState(false);
    const [activityDateRange, setActivityDateRange] = React.useState<[Date | null, Date | null]>([null, null]);
    const [activityEventType, setActivityEventType] = React.useState('');
    const [activityEventCategory, setActivityEventCategory] = React.useState('');
    const [activityFeedPage, setActivityFeedPage] = React.useState(0);
    const activityFeedRequestIdRef = React.useRef(0);
    const activityFeedLoadingMoreRef = React.useRef(false);

    const lottoState = useStoreDetailsOverviewLotto({
        store,
        onSilentStoreRefresh,
    });
    const agentState = useStoreDetailsOverviewAgent({
        onSilentStoreRefresh,
        selectedPosType,
        store,
    });
    const posState = useStoreDetailsOverviewPos({
        agentConfig: agentState.agentConfig,
        onSilentStoreRefresh,
        posService: lottoState.posService,
        refreshAgentConfig: agentState.refreshAgentConfig,
        store,
    });
    const syncGamesState = useStoreDetailsOverviewSyncGames({
        onSilentStoreRefresh,
        store,
    });
    const agentConfigFormHandleSubmit = agentState.agentConfigForm.handleSubmit;
    const agentConfigFormSetValue = agentState.agentConfigForm.setValue;
    const agentConfigFormIsValid = agentState.agentConfigForm.isValid;
    const setLogsDialogOpen = agentState.setLogsDialogOpen;
    const setPosDialogOpen = posState.setPosDialogOpen;
    const setPosEnabled = posState.setPosEnabled;

    const loadActivityFeedPage = React.useCallback(
        async (page: number, append: boolean) => {
            if (!store?.id) {
                return;
            }

            const requestId = activityFeedRequestIdRef.current;

            if (append) {
                setActivityFeedLoadingMore(true);
            } else {
                setActivityFeedLoading(true);
            }

            try {
                const response = await activityFeedService.getActivityFeed(store.id, {
                    limit: 100,
                    offset: page * 100,
                    startDate: activityDateRange[0],
                    endDate: activityDateRange[1],
                    eventType: activityEventType,
                    eventCategory: activityEventCategory,
                });

                if (requestId !== activityFeedRequestIdRef.current) {
                    return;
                }

                setActivityFeed((previousFeed) => (append ? [...previousFeed, ...response.events] : response.events));

                const loadedCount = (page * 100) + response.events.length;
                setActivityFeedHasMore(response.events.length > 0 && loadedCount < response.total);
                setActivityFeedPage(page);
            } catch (error) {
                if (requestId !== activityFeedRequestIdRef.current) {
                    return;
                }

                console.error('Error loading activity feed:', error);

                if (!append) {
                    setActivityFeed([]);
                }

                setActivityFeedHasMore(false);
            } finally {
                if (requestId !== activityFeedRequestIdRef.current) {
                    return;
                }

                if (append) {
                    setActivityFeedLoadingMore(false);
                    activityFeedLoadingMoreRef.current = false;
                } else {
                    setActivityFeedLoading(false);
                }
            }
        },
        [activityDateRange, activityEventCategory, activityEventType, store?.id],
    );

    React.useEffect(() => {
        if (!store?.id) {
            setActivityFeed([]);
            setActivityFeedHasMore(false);
            setActivityFeedLoading(false);
            setActivityFeedLoadingMore(false);
            return;
        }

        activityFeedRequestIdRef.current += 1;
        activityFeedLoadingMoreRef.current = false;
        setActivityFeed([]);
        setActivityFeedPage(0);
        setActivityFeedHasMore(false);
        setActivityFeedLoadingMore(false);
        void loadActivityFeedPage(0, false);
    }, [activityDateRange, activityEventCategory, activityEventType, loadActivityFeedPage, store?.id]);

    const loadNextActivityFeedPage = React.useCallback(async () => {
        if (
            activityFeedLoading ||
            activityFeedLoadingMore ||
            activityFeedLoadingMoreRef.current ||
            !activityFeedHasMore
        ) {
            return;
        }

        activityFeedLoadingMoreRef.current = true;
        await loadActivityFeedPage(activityFeedPage + 1, true);
    }, [activityFeedHasMore, activityFeedLoading, activityFeedLoadingMore, activityFeedPage, loadActivityFeedPage]);

    const handleActivityFeedScroll = React.useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (!activityFeedHasMore || activityFeedLoading || activityFeedLoadingMore) {
                return;
            }

            const container = event.currentTarget;
            const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
            const thresholdPx = 25 * 52;

            if (distanceFromBottom <= thresholdPx) {
                void loadNextActivityFeedPage();
            }
        },
        [activityFeedHasMore, activityFeedLoading, activityFeedLoadingMore, loadNextActivityFeedPage],
    );

    const handleActivityDateRangeChange = React.useCallback((range: [Date | null, Date | null]) => {
        setActivityDateRange(range);
    }, []);

    const handleActivityEventTypeChange = React.useCallback((eventType: string) => {
        setActivityEventType(eventType);
    }, []);

    const handleActivityEventCategoryChange = React.useCallback((eventCategory: string) => {
        setActivityEventCategory(eventCategory);
    }, []);

    const handleSaveAgentConfig = React.useCallback(() => {
        void agentConfigFormHandleSubmit(agentState.handleSaveAgentConfig)();
    }, [agentConfigFormHandleSubmit, agentState.handleSaveAgentConfig]);

    const handleOpenPosDialog = React.useCallback(() => {
        setPosDialogOpen(true);
    }, [setPosDialogOpen]);

    const handleClosePosDialog = React.useCallback(() => {
        setPosDialogOpen(false);
    }, [setPosDialogOpen]);

    const handleCloseLogsDialog = React.useCallback(() => {
        setLogsDialogOpen(false);
    }, [setLogsDialogOpen]);

    return {
        activityDateRange,
        activityEventCategory,
        activityEventCategoryOptions: ACTIVITY_FEED_EVENT_CATEGORY_OPTIONS,
        activityEventType,
        activityEventTypeOptions: ACTIVITY_FEED_EVENT_TYPE_OPTIONS,
        activityFeed,
        activityFeedHasMore,
        activityFeedLoading,
        activityFeedLoadingMore,
        activityFeedPage,
        agentConfig: agentState.agentConfig,
        agentConfigForm: {
            control: agentState.agentConfigForm.control,
            handleSubmit: agentConfigFormHandleSubmit,
            isValid: agentConfigFormIsValid,
            setValue: agentConfigFormSetValue,
        },
        agentDeviceId: agentState.agentDeviceId,
        agentLastFetchedLabel: agentState.agentLastFetchedLabel,
        agentLastSeenAt: agentState.agentLastSeenAt,
        agentLastSeenLabel: agentState.agentLastSeenLabel,
        availableExtensions: agentState.availableExtensions,
        changingDevice: agentState.changingDevice,
        canRetryVerifoneLogin: agentState.canRetryVerifoneLogin,
        handleRetryVerifoneLogin: agentState.handleRetryVerifoneLogin,
        retryingVerifoneLogin: agentState.retryingVerifoneLogin,
        verifoneLoginRetryNeedsSave: agentState.verifoneLoginRetryNeedsSave,
        checkboxCheckedIcon: agentState.checkboxCheckedIcon,
        checkboxIcon: agentState.checkboxIcon,
        getAvatarColor,
        getInitials,
        handleActivityDateRangeChange,
        handleChangeDevice: agentState.handleChangeDevice,
        handleDownloadLogs: agentState.handleDownloadLogs,
        handleActivityEventCategoryChange,
        handleActivityEventTypeChange,
        handleActivityFeedScroll,
        handleCloseLottoBackfill: lottoState.handleCloseLottoBackfill,
        handleEditLottoPassword: lottoState.handleEditLottoPassword,
        handleFetchLatestLogs: agentState.handleFetchLatestLogs,
        handleLottoBackfillSubmit: lottoState.handleLottoBackfillSubmit,
        handleLottoEnabledChange: lottoState.handleLottoEnabledChange,
        handleOpenLottoBackfill: lottoState.handleOpenLottoBackfill,
        handleRevealLottoPassword: lottoState.handleRevealLottoPassword,
        handleScratchersBulkSync: lottoState.handleScratchersBulkSync,
        handleOpenPosDialog,
        handleSaveAgentConfig,
        handleSaveLottoCredentials: lottoState.handleSaveLottoCredentials,
        handleSaveLottoRetailerId: lottoState.handleSaveLottoRetailerId,
        handleSaveLottoSetup: lottoState.handleSaveLottoSetup,
        handleSavePosSetup: posState.handleSavePosSetup,
        hasAgentDeviceId: agentState.hasAgentDeviceId,
        isAgentLoading: agentState.isAgentLoading,
        logsDialogOpen: agentState.logsDialogOpen,
        lottoBackfillDays: lottoState.lottoBackfillDays,
        lottoBackfillDialogOpen: lottoState.lottoBackfillDialogOpen,
        lottoBackfillForceFetch: lottoState.lottoBackfillForceFetch,
        lottoBackfillLoading: lottoState.lottoBackfillLoading,
        lottoBackfillMode: lottoState.lottoBackfillMode,
        lottoBackfillShowForceFetch: lottoState.lottoBackfillShowForceFetch,
        scratcherBulkSyncDays: lottoState.scratcherBulkSyncDays,
        scratcherBulkSyncLoading: lottoState.scratcherBulkSyncLoading,
        handleSyncGamesToPosChange: syncGamesState.handleSyncGamesToPosChange,
        lottoDialogOpen: lottoState.lottoDialogOpen,
        lottoEditMode: lottoState.lottoEditMode,
        lottoEnabled: lottoState.lottoEnabled,
        lottoCredentialStatusDisplay: lottoState.lottoCredentialStatusDisplay,
        lottoCommittedUsername: lottoState.lottoCommittedUsername,
        lottoForm: lottoState.lottoForm,
        lottoLastSeenLabel: lottoState.lottoLastSeenLabel,
        lottoPasswordLastRotatedLabel: lottoState.lottoPasswordLastRotatedLabel,
        lottoPasswordRevealing: lottoState.lottoPasswordRevealing,
        lottoPasswordVisible: lottoState.lottoPasswordVisible,
        lottoSaving: lottoState.lottoSaving,
        onCloseLottoDialog: lottoState.handleCloseLottoDialog,
        onCloseLogsDialog: handleCloseLogsDialog,
        onClosePosDialog: handleClosePosDialog,
        posDialogOpen: posState.posDialogOpen,
        posEnabled: posState.posEnabled,
        posLastSeenLabel: posState.posLastSeenLabel,
        posSaving: posState.posSaving,
        syncGamesToPosEnabled: syncGamesState.syncGamesToPosEnabled,
        syncGamesToPosUpdating: syncGamesState.syncGamesToPosUpdating,
        saving: agentState.saving,
        setLogsDialogOpen,
        setLottoBackfillDays: lottoState.setLottoBackfillDays,
        setLottoBackfillForceFetch: lottoState.setLottoBackfillForceFetch,
        setLottoEditMode: lottoState.setLottoEditMode,
        setLottoPassword: lottoState.setLottoPassword,
        setLottoRetailerId: lottoState.setLottoRetailerId,
        setLottoUsername: lottoState.setLottoUsername,
        setPosDialogOpen,
        setPosEnabled,
        showAgentNotFound: agentState.showAgentNotFound,
        showLastSeen: agentState.showLastSeen,
        storeId: store?.id,
    };
};
